/**
 * /fr/retranscription-entretien — interview-transcription reframe bridge (FR).
 *
 * Targets retranscription entretien (300) / transcription entretien /
 * retranscrire un entretien. Reframe: le verbatim n'est pas le livrable.
 */
import { MarketingShell } from './MarketingShell';

export function FrRetranscriptionEntretien() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/retranscription-entretien"
      hreflangLocales={['fr']}
      documentTitle="Retranscrire un entretien utilisateur (et en tirer des décisions) · Draftlize"
      documentDescription="Comment retranscrire un entretien rapidement et gratuitement — puis transformer le verbatim en décisions produit exploitables, au lieu de le laisser dormir."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Entretiens</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Retranscrire un entretien —<br />
            <em>puis en faire quelque chose.</em>
          </h1>
          <p className="lede">
            Enregistrement, et hop : une transcription propre en quelques secondes. Les outils de retranscription — Notta, Tactiq, tl;dv — sont excellents pour capturer ce qui a été dit. Continuez à les utiliser. Mais le verbatim est une <em>matière première</em>, pas un livrable. Ce qui comptait dans cet entretien — l'insight, puis la <em>décision produit</em> qu'il a déclenchée — se noie dans des milliers de mots, atterrit dans un fichier que personne ne rouvre, et devient caduc en quelques semaines. Cette page parle de l'étape d'après.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Retranscrire un entretien, gratuitement.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Récupérez l'audio</strong> — enregistrement d'appel, mémo vocal, MP3.</li>
            <li><strong>Lancez la transcription</strong> — un outil IA convertit la parole en texte, souvent avec un quota gratuit.</li>
            <li><strong>Relisez</strong> — corrigez les noms propres, puis exportez le verbatim.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            Vous avez votre transcription. Et maintenant ? C'est là que 90 % du travail s'arrête — et que la valeur de l'entretien s'évapore. Capturer n'est plus le goulot d'étranglement. <em>Extraire</em> l'est.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Matière première vs contexte vivant</span>
            <h2 className="serif">Là où la transcription s'arrête, <em>le travail commence.</em></h2>
          </div>
          <p>Votre outil de transcription et Draftlize font des métiers différents. L'un capture l'entretien ; l'autre garde vivantes les décisions qui en sortent. Gardez les deux.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Outil de transcription</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Rôle</td><td>Capturer ce qui a été dit</td><td className="ours yes">Garder à jour ce qui a été décidé</td></tr>
            <tr><td>Sortie</td><td className="partial">Verbatim + résumé</td><td className="ours yes">Cartes de décision dans un graphe</td></tr>
            <tr><td>Un insight change un choix</td><td className="no">Deux notes sans lien</td><td className="ours yes">Liées ; l'ancienne passe en « stale »</td></tr>
            <tr><td>Trois semaines plus tard</td><td className="no">Enseveli dans un fichier</td><td className="ours yes">Adressable, à jour ou signalé</td></tr>
            <tr><td>Un agent IA peut s'en servir</td><td className="partial">Lit du texte brut</td><td className="ours yes">Lit + écrit via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">De l'entretien à la décision produit.</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Laissez la capture à votre outil. Le geste qui compte, c'est ce qui arrive aux quelques décisions issues de vos entretiens — dans Draftlize, elles cessent d'être des lignes dans un verbatim et deviennent un contexte produit qui reste vrai. Le verbatim alimente le <a href="/fr/prd-document-exigences-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a> ; la décision, elle, doit lui survivre.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Les décisions deviennent des cartes</h3>
            <p>Collez le résumé de l'entretien : les décisions à l'intérieur deviennent des cartes typées — la décision, sa justification, le verbatim qui l'appuie, le responsable. L'heure d'entretien se réduit à ce qui doit lui survivre.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Reliez le verbatim à l'exigence</h3>
            <p>Chaque citation d'utilisateur reste liée à la décision qu'elle a déclenchée et à l'exigence qui en découle. Quand l'insight est contredit par un entretien suivant, ce qui en dépend passe en « stale ».</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Votre agent IA lit ce qui a été décidé</h3>
            <p>Les décisions issues de vos entretiens deviennent un contexte que Claude Code ou Cursor lisent via MCP — l'agent qui rédige la prochaine spec sait déjà ce que les utilisateurs ont dit, et ce que vous en avez conclu.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Votre outil retranscrit l'entretien à la perfection. Il ne peut juste pas vous dire, trois semaines après, quelles décisions qui en sont sorties tiennent encore.
        </blockquote>
        <cite>Gardez l'outil de transcription. Laissez Draftlize garder vivantes les décisions.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Comment retranscrire un entretien gratuitement ?', a: 'La plupart des outils de transcription (Notta, tl;dv, Tactiq…) offrent un quota gratuit. Draftlize ne transcrit pas : il intervient une fois le verbatim obtenu, pour en extraire les décisions et les garder à jour.' },
            { q: 'Quel format pour une retranscription ?', a: 'Texte brut horodaté suffit pour un entretien utilisateur. L’important n’est pas le format du verbatim, mais ce que vous en faites ensuite.' },
            { q: 'Que faire de la transcription une fois obtenue ?', a: 'Collez le résumé dans Draftlize et capturez les décisions à l’intérieur sous forme de cartes — décision, justification, citation d’appui. Le but n’est pas de stocker tout le verbatim, mais de garder adressables les quelques décisions qui comptent.' },
            { q: 'En quoi est-ce différent d’un simple dossier de notes ?', a: 'Dans un dossier, les décisions restent du texte plat, déconnecté des specs qu’elles affectent. Elles vieillissent en silence. Draftlize les relie et signale ce qui devient obsolète quand vous changez d’avis.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Vos entretiens méritent mieux qu'un fichier oublié <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Gardez votre outil de transcription. Déposez le résumé dans Draftlize : les décisions issues de vos entretiens deviennent des cartes vivantes — liées, à jour et lisibles par votre agent IA.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
